// istanbul ignore file

import { ReportedFindingData } from './reported-findings';
import { IntegratedCompliancePropertyStatus, IntegratedComplianceServiceStatus, IntegrationSoftwareCodes } from './integration';
import { Notice } from './notices';
import { PropertyKeyInformation } from './property-key-information';
import { PropertyManager } from './property-manager';
import { ServicePriorityLevel, Skill } from './servicing';
import { LeaseOccupant } from './tenancies';

export type PortfolioService = {
    serviceId: number;
    serviceName: string;
    serviceFrequency: string;
    shortName: string;
    inspectionStatusId: number;
    dueDate: string;
    inspectionStatusName: string;
    lastStandardBookingDate: string | null;
    lastFullServiceBookingDate: string | null;
    currentStandardBookingDate: string | null;
    currentFurtherWorksBookingDate: string | null;
    lastCancelledBookingDate: string | null;
    skills: Skill[];
    priorityLevels: ServicePriorityLevel[];
    tags: string[];
    holdReason?: string;
    reportedFindings?: ReportedFindingData[];
};

export type PortfolioProperty = {
    propertyId: number;
    keyNumber: string | null;
    keyLocationId: number | null;
    isKeyAvailable: boolean;
    propertyKeyInformation: PropertyKeyInformation;
    pricingPackageName: string | null;
    productName: string | null;
    softwareName: string | null;
    softwareVersions: SoftwareVersions | null;
    address: string;
    addressLine1: string;
    addressLine2: string;
    occupants: LeaseOccupant[];
    notices: Notice[];
    propertyManager: PropertyManager | null;
    services: PortfolioService[];
    accessIssues: {
        suspensionLevel: number | string;
        problems: {
            createdAt: string;
            publicName: string;
            problemLabel: string;
        }[];
    };
    tags: string[];
    addressToSort: string;
    subscription?: {
        isSubscribed: boolean;
        subscribedAt: string | null;
    };
};

export type SoftwareVersion = {
    id: number;
    name: string;
};

export type SoftwareVersions = {
    primary: SoftwareVersion;
    secondary: SoftwareVersion[];

};

export type AgencyPortfolioProperty = {
    portfolioPropertyId: number;
    address: string;
    softwareName: string;
    firstActiveDate: string;
    lastActiveDate: string;
    createdAt: string;
    isUnderEnrolment: boolean;
    underEnrolmentAt: string | null;
    underEnrolmentBy: string | null;
    tags: string[];
};

export type IntegratedProperty = AgencyPortfolioProperty & {
    isLost: boolean;
    propertyManagerName: string | null;
    propertyManagerRef: string | null;
    softwareCode: IntegrationSoftwareCodes;
    complianceStatus: IntegratedCompliancePropertyStatus;
    services: IntegratedPropertyService[];
    gasApplianceCount: number | null;
    hasGasAppliances: boolean;
    gasApplianceCountValue?: GasApplianceValues;
    isResidentialProperty: boolean;
    nonResidentialPropertyType: string | null;
};

export type IntegratedPropertyService = {
    complianceId: number | null;
    complianceRequirementId: number;
    name: string;
    shortName: string;
    complianceStatus: IntegratedComplianceServiceStatus;
    serviceFrequencyMonths: number;
    lastServiceDate: string | null;
    nextServiceDate: string | null;
    providedBy: string | null;
    managedBy: IntegratedServiceManager;
    isRequired: boolean;
    isFurtherWorksRequired: number | null;
    tags: string[];
    supplier: { id: number; supplier_name: string };
};

export const GAS_APPLIANCE_VALUES = {
    UNKNOWN_COUNT: 'unknownCount',
    NONE: 0,
    ONE: 1,
    TWO_OR_MORE: 2,
};

export type GasApplianceValues = typeof GAS_APPLIANCE_VALUES[keyof typeof GAS_APPLIANCE_VALUES];

type GasApplianceNumber = Extract<GasApplianceValues, number>;

export type IntegratedPropertyUpdate = {
    gasApplianceCount?: GasApplianceNumber | null;
    isResidentialProperty?: boolean;
    nonResidentialPropertyType?: string;
    hasGasAppliances?: boolean | null;
};

export enum IntegratedServiceManager {
    Landlord = 'LANDLORD',
    ThirdParty = 'THIRD_PARTY',
    Unknown = 'UNKNOWN',
    NotManaged = 'NOT_MANAGED'
}

export enum IntegratedPortfolioFacets {
    Compliant = 'COMPLIANT',
    NonCompliant = 'NON_COMPLIANT',
    ApproachingNextService = 'APPROACHING_NEXT_SERVICE',
    Overdue = 'OVERDUE',
    MissingSupplier = 'MISSING_SUPPLIER',
    MissingLastServiceDate = 'MISSING_LAST_SERVICE_DATE',
    RecentlyAdded = 'RECENTLY_ADDED',
    UnderEnrolment = 'UNDER_ENROLMENT',
    NonResidentialProperty = 'NON_RESIDENTIAL_PROPERTY',
    GasConfirmationRequired = 'GAS_CONFIRMATION_REQUIRED',
    FurtherWorksRequired = 'FURTHER_WORKS_REQUIRED'
}

export enum PortfolioPropertyTag {
    Cancelled = 'CANCELLED',
    OutstandingInvoices = 'OUTSTANDING_INVOICES',
    SaferHomePackage = 'SAFER_HOME_PACKAGE',
    Vacant = 'VACANT',
    OverdueInvoices = 'OVERDUE_INVOICES',
    Integrated = 'INTEGRATED',
    PAYGBilling = 'PAYG_BILLING',
    SubscriptionBilling = 'SUBSCRIPTION_BILLING',
    BetweenTenancyTracking = 'BETWEEN_TENANCY_TRACKING',
    ExternalActiveNotices = 'EXTERNAL_ACTIVE_NOTICES',
    LeaseAgreementUpdateRequired = 'LEASE_AGREEMENT_UPDATE_REQUIRED',
    LegislationUpgradePending = 'LEGISLATION_UPGRADE_PENDING',
    NotEnrolled = 'NOT_ENROLLED',
    AccessIssues = 'ACCESS_ISSUES',
    NetStateCancellation = 'NET_STATE_CANCELLATION',
    ComplianceIssues = 'COMPLIANCE_ISSUES',
    PendingItems = 'PENDING_ITEMS',
    LegacyPendingItems = 'LEGACY_PENDING_ITEMS'
}

export enum PortfolioServiceTag {
    Overdue = 'OVERDUE',
    ServicePending = 'SERVICE_PENDING',
    BookedIn = 'BOOKED_IN',
    RebookingInProgress = 'REBOOKING_IN_PROGRESS',
    AwaitingQuotationApproval = 'AWAITING_QUOTATION_APPROVAL',
    PaymentPending = 'PAYMENT_PENDING',
    UpdatesRequired = 'UPDATES_REQUIRED',
    OnHold = 'ON_HOLD',
    Cancelled = 'CANCELLED',
    FixedPackage = 'FIXED_PACKAGE',
    MultiGas = 'MULTI_GAS',
    CordedBlinds = 'CORDED_BLINDS',
    RCD = 'RCD_CHECK',
    WaterEfficiency = 'WATER_EFFICIENCY',
    HasUrgentQuotations = 'HAS_URGENT_QUOTATIONS',
    UrgentPriorityLevel = 'URGENT_PRIORITY_LEVEL',
    Compliant = 'COMPLIANT',
    ComplianceIssues = 'COMPLIANCE_ISSUES',
    PendingItems = 'PENDING_ITEMS',
    LegacyPendingItems = 'LEGACY_PENDING_ITEMS'
}

export enum PortfolioFacets {
    NoLongerOnService = 'NO_LONGER_ON_SERVICE',
    OnService = 'ON_SERVICE',
    OutstandingInvoices = 'OUTSTANDING_INVOICES',
    OverdueInvoices = 'OVERDUE_INVOICES',
    ConfirmedBooking = 'CONFIRMED_BOOKING',
    BookingInProgress = 'BOOKING_IN_PROGRESS',
    ReschedulingBooking = 'RESCHEDULING_BOOKING',
    ActiveNotices = 'ACTIVE_NOTICES',
    AccessIssues = 'ACCESS_ISSUES',
    Vacant = 'VACANT',
    OnHold = 'ON_HOLD',
    DueInNext = 'DUE_IN_NEXT',
    Overdue = 'OVERDUE',
    NotOverdue = 'NOT_OVERDUE',
    SaferHomePlan = 'SAFER_HOME_PLAN',
    PAYG = 'PAYG',
    UrgentQuotes = 'URGENT_QUOTES',
    NonUrgentQuotes = 'NON_URGENT_QUOTES',
    PriorityBooking = 'PRIORITY_BOOKING',
    CompliantStatus = 'COMPLIANT_STATUS',
    ComplianceIssuesStatus = 'COMPLIANCE_ISSUES_STATUS',
    BookingInProgressStatus = 'BOOKING_IN_PROGRESS_STATUS',
    NetStateCancellation = 'NET_STATE_CANCELLATION',
    NotIntegrated = 'NOT_INTEGRATED',
    PendingItemsStatus = 'PENDING_ITEMS_STATUS',
    CancelledProperties = 'CANCELLED_PROPERTIES',
    SubscribedProperties = 'SUBSCRIBED_PROPERTIES'
}

export enum ServiceExclusivity {
    Including = 'INCLUDING',
    Only = 'ONLY',
    Excluding = 'EXCLUDING'
}
